import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, ChevronDown, GlobeIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useLanguage } from '../../context/LanguageContext';
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const links = [{
    name: t('navbar.home'),
    path: '/'
  }, {
    name: t('navbar.about'),
    path: '/about'
  }, {
    name: t('navbar.contact'),
    path: '/contact'
  }];
  const serviceLinks = [{
    name: t('navbar.servicesDropdown.overview'),
    path: '/services'
  }, {
    name: t('navbar.servicesDropdown.courses'),
    path: '/services/courses'
  }, {
    name: t('navbar.servicesDropdown.workshops'),
    path: '/services/workshops'
  }, {
    name: t('navbar.servicesDropdown.talks'),
    path: '/services/talks'
  }];
  const isActive = (path: string) => location.pathname === path;
  const isServicesActive = () => location.pathname.startsWith('/services');
  const toggleLanguage = () => setLanguage(language === 'is' ? 'en' : 'is');
  return <nav className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md border-b border-gray-100" data-id="element-82">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-83">
        <div className="flex justify-between items-center h-20" data-id="element-84">
          <Link to="/" className="flex items-center gap-2 group" data-id="element-85">
            <span className="font-signature text-3xl text-teal" data-id="element-86">HelgiG</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8" data-id="element-87">
            {links.slice(0, 2).map(link => <Link key={link.path} to={link.path} className={`relative font-medium transition-colors hover:text-coral ${isActive(link.path) ? 'text-coral' : 'text-gray-600'}`} data-id="element-88">
                {link.name}
                {isActive(link.path) && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-89" />}
              </Link>)}

            {/* Services Dropdown */}
            <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)} data-id="element-90">
              <button className={`relative font-medium transition-colors hover:text-coral flex items-center gap-1 ${isServicesActive() ? 'text-coral' : 'text-gray-600'}`} data-id="element-91">
                {t('navbar.services')}
                <ChevronDown size={16} className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} data-id="element-92" />
                {isServicesActive() && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-93" />}
              </button>

              <AnimatePresence data-id="element-94">
                {servicesOpen && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 8
              }} transition={{
                duration: 0.15
              }} className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 overflow-hidden" data-id="element-95">
                    {serviceLinks.map(link => <Link key={link.path} to={link.path} className={`block px-4 py-2.5 text-sm font-medium transition-colors ${isActive(link.path) ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50 hover:text-coral'}`} data-id="element-96">
                        {link.name}
                      </Link>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            <Link to="/contact" className={`relative font-medium transition-colors hover:text-coral ${isActive('/contact') ? 'text-coral' : 'text-gray-600'}`} data-id="element-97">
              {t('navbar.contact')}
              {isActive('/contact') && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-98" />}
            </Link>

            <button onClick={toggleLanguage} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-sm font-medium text-gray-600 hover:border-teal hover:text-teal transition-colors" aria-label={language === 'is' ? 'Switch to English' : 'Skipta yfir á íslensku'} data-id="element-99">
              <GlobeIcon size={16} data-id="element-100" />
              <span data-id="element-101">{language === 'is' ? 'EN' : 'IS'}</span>
            </button>

            <Link to="/contact" data-id="element-102">
              <Button size="sm" variant="secondary" data-id="element-103">
                {t('navbar.bookNow')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-3" data-id="element-104">
            <button onClick={toggleLanguage} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-gray-200 text-sm font-medium text-gray-600 hover:border-teal hover:text-teal transition-colors" aria-label={language === 'is' ? 'Switch to English' : 'Skipta yfir á íslensku'} data-id="element-105">
              <GlobeIcon size={14} data-id="element-106" />
              <span data-id="element-107">{language === 'is' ? 'EN' : 'IS'}</span>
            </button>
            <button onClick={() => setIsOpen(!isOpen)} className="text-gray-600 hover:text-coral transition-colors" data-id="element-108">
              {isOpen ? <X size={28} data-id="element-109" /> : <Menu size={28} data-id="element-110" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence data-id="element-111">
        {isOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-white border-b border-gray-100 overflow-hidden" data-id="element-112">
            <div className="px-4 pt-2 pb-6 space-y-2" data-id="element-113">
              {links.slice(0, 2).map(link => <Link key={link.path} to={link.path} onClick={() => setIsOpen(false)} className={`block px-4 py-3 rounded-xl font-medium ${isActive(link.path) ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-114">
                  {link.name}
                </Link>)}

              <div data-id="element-115">
                <button onClick={() => setServicesOpen(!servicesOpen)} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium ${isServicesActive() ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-116">
                  {t('navbar.services')}
                  <ChevronDown size={18} className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} data-id="element-117" />
                </button>
                {servicesOpen && <div className="pl-4 pt-1 space-y-1 animate-fade-in" data-id="element-118">
                    {serviceLinks.map(link => <Link key={link.path} to={link.path} onClick={() => {
                setIsOpen(false);
                setServicesOpen(false);
              }} className={`block px-4 py-2.5 rounded-lg text-sm font-medium ${isActive(link.path) ? 'bg-coral/5 text-coral' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`} data-id="element-119">
                        {link.name}
                      </Link>)}
                  </div>}
              </div>

              <Link to="/contact" onClick={() => setIsOpen(false)} className={`block px-4 py-3 rounded-xl font-medium ${isActive('/contact') ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-120">
                {t('navbar.contact')}
              </Link>

              <div className="pt-2 px-4" data-id="element-121">
                <Link to="/contact" onClick={() => setIsOpen(false)} data-id="element-122">
                  <Button className="w-full" variant="secondary" data-id="element-123">
                    {t('navbar.bookNow')}
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}